import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { graphql } from '../../../../gql'
import { useGQLMutation } from '../../../../hooks/GQLUtilityHooks'
import { toastError, toastSuccess, toastWarning } from '../../../../lib/toastHelpers'
import { useSettingsStore } from '../../../../contexts/SettingsContext'
import { useCore } from '../../../../hooks'
import { wait, printTicket } from '../../../../lib'
import { subscribeToPickingActivity } from './pickingActivityEvents'
import {
  ProcessStatus,
  ProcessStatusOrder,
  UseProcessStatusResult,
  PROCESS_STATUS_LABELS,
  PROCESS_STATUS_DELAY,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  TOAST_CONFIG,
} from './processStatusTypes'
import {
  getUnpickedItems,
  getUnpackedItems,
  checkCanUpdateProcessStatus,
  getAvailableBackSteps,
  getCurrentProcessStatus,
  validateOrderForPackageInit,
  buildPackageInitErrorMessage,
  hasPrintErrors,
  getRedirectPath,
  getStatusBeforeError,
} from './processStatusHelpers'

// GraphQL Mutations
const updateOrderMutation = graphql(`
  mutation UpdateOrderProcessStatus($id: Int!, $status: String!, $note: String) {
    updateOrder(id: $id, processStatusRelation: { status: $status, note: $note }) {
      data {
        processStatus {
          id
        }
      }
      errors
    }
  }
`)

const sendPackageMutation = graphql(`
  mutation SendPackageMutation($id: Int!) {
    sendPackage(id: $id) {
      errors
      data {
        id
        ticketUrl
        carrier {
          id
        }
      }
    }
  }
`)

// Prevent re-entry during auto-transitions (needs to be outside component)
let isProcessingChanges = false

/**
 * Hook for managing order process status transitions
 * Handles status updates, validations, and side effects like printing and sending packages
 */
export function useProcessStatus(currentOrder: ProcessStatusOrder | null | undefined): UseProcessStatusResult {
  // Hooks
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { moveOrderStatusRightAfterPicking: autoProcessStatusChange } = useSettingsStore()
  const { core } = useCore()

  const pendingAutoTransition = useRef(false)
  const blockedAutoTransition = useRef<'packageValidation' | 'missingCarrier' | null>(null)

  // Extract URL parameters
  const searchParams = new URLSearchParams(location.search)
  const group = searchParams.get('group')

  // Process status calculations
  const currentProcessStatus = getCurrentProcessStatus(currentOrder)
  const unpickedItems = useMemo(() => getUnpickedItems(currentOrder?.clearanceItems), [currentOrder?.clearanceItems])
  const unpackedItems = useMemo(() => getUnpackedItems(currentOrder?.items), [currentOrder?.items])

  // Computed values
  const canUpdateProcessStatus = useMemo(
    () => checkCanUpdateProcessStatus(currentProcessStatus, unpickedItems, unpackedItems, currentOrder?.carrier?.id),
    [currentProcessStatus, unpickedItems, unpackedItems, currentOrder?.carrier?.id],
  )

  const btnNextStatusLabel = PROCESS_STATUS_LABELS[currentProcessStatus] || 'Vráceno'

  const backSteps = useMemo(() => getAvailableBackSteps(currentProcessStatus), [currentProcessStatus])

  const statusBeforeError = useMemo(() => getStatusBeforeError(currentOrder), [currentOrder?.processStatusRelations])

  // Mutations
  const { mutateAsync: mutateProcessStatus, isPending: isLoading } = useGQLMutation(
    updateOrderMutation,
    ['updateOrder', currentOrder?.id],
    {
      onError: (error: Error) => {
        toastError(`${ERROR_MESSAGES.UPDATE_ERROR}: ${error.message}`)
      },
    },
  )

  // sendPackages owns the error toast, including errors while processing the response.
  const { mutateAsync: mutateSendPackage } = useGQLMutation(sendPackageMutation, ['sendPackage', currentOrder?.id], {
    showErrorToast: false,
  })

  /**
   * Send unsent packages and collect ticketUrls from mutation responses
   */
  const sendPackages = useCallback(
    async (packages: ProcessStatusOrder['packages']) => {
      if (!packages || packages.length === 0) return { success: true, packageTicketUrls: new Map() }

      let hasErrors = false
      const packageTicketUrls = new Map<string, { ticketUrl: string; carrierId: string }>()

      for (const pkg of packages) {
        // Send unsent packages
        if (!pkg.sent) {
          try {
            const result = await mutateSendPackage({ id: parseInt(pkg.id) })
            console.log('Package sent:', result)

            // Collect ticketUrl from mutation response
            if (result.sendPackage?.data?.ticketUrl) {
              packageTicketUrls.set(pkg.id, {
                ticketUrl: result.sendPackage.data.ticketUrl,
                carrierId: result.sendPackage.data.carrier?.id || pkg.carrier?.id || '',
              })
            }
          } catch (error) {
            const message =
              (error instanceof Error ? error.message : typeof error === 'string' ? error : '') || 'Něco se pokazilo'
            toastError(`${ERROR_MESSAGES.CARRIER_ERROR}: ${message}`)
            hasErrors = true
          }
        }

        if (hasErrors) {
          return { success: false, packageTicketUrls: new Map() }
        }
      }

      return { success: !hasErrors, packageTicketUrls }
    },
    [mutateSendPackage],
  )

  /**
   * Print tickets for packages
   */
  const printPackageTickets = useCallback(
    async (
      packages: ProcessStatusOrder['packages'],
      packageTicketUrls?: Map<string, { ticketUrl: string; carrierId: string }>,
    ) => {
      if (!packages || packages.length === 0) return { success: true }

      let hasErrors = false

      for (const pkg of packages) {
        // Use ticketUrl from mutation response if available, otherwise fall back to package data
        const ticketData = packageTicketUrls?.get(pkg.id)
        const ticketUrl = ticketData?.ticketUrl || pkg.ticketUrl
        const carrierId = ticketData?.carrierId || pkg.carrier?.id || ''

        if (!ticketUrl) {
          console.warn(`No ticketUrl available for package ${pkg.id}`)
          hasErrors = true
          continue
        }

        // Print ticket using ticketUrl from mutation response
        const printResult = await printTicket(
          {
            ticketUrl,
            carrier: { id: carrierId },
          },
          core,
        )
        if (hasPrintErrors(printResult)) {
          hasErrors = true
        }

        await wait()
      }

      await wait(PROCESS_STATUS_DELAY.PRINT_WAIT)

      return { success: !hasErrors }
    },
    [core],
  )

  /**
   * Process and send packages before marking as packed
   */
  const processPackagesForPacked = useCallback(
    async (packages: ProcessStatusOrder['packages']) => {
      const sendResult = await sendPackages(packages)
      if (!sendResult.success) {
        return { success: false }
      }

      const printResult = await printPackageTickets(packages)
      return printResult
    },
    [sendPackages, printPackageTickets],
  )

  /**
   * Save process status on server, then refresh data, redirect and show toast
   */
  const commitProcessStatus = useCallback(
    async (status: ProcessStatus, note?: string): Promise<boolean> => {
      try {
        const result = await mutateProcessStatus({
          id: parseInt(currentOrder?.id || '0'),
          status,
          ...(note ? { note } : {}),
        })

        // Check for errors in the response
        const errors = result.updateOrder?.errors
        if (errors && Array.isArray(errors) && errors.length > 0) {
          toastError(`${ERROR_MESSAGES.UPDATE_ERROR}: ${errors.join(', ')}`)
          return false
        }

        // Success - invalidate queries, redirect, show toast
        queryClient.invalidateQueries({ queryKey: ['order', { id: currentOrder?.id }] })
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        history.push(getRedirectPath(currentProcessStatus, group))
        toastSuccess(SUCCESS_MESSAGES.STATUS_UPDATED)
        return true
      } catch (error) {
        // Error already handled by onError callback
        return false
      }
    },
    [currentOrder?.id, mutateProcessStatus, queryClient, history, currentProcessStatus, group],
  )

  /**
   * Update process status with validation
   */
  const updateProcessStatus = useCallback(
    async (nextStatus: ProcessStatus, note?: string) => {
      // Validate transitions
      if (nextStatus === 'packing' && unpickedItems.length > 0) {
        toastError(ERROR_MESSAGES.UNPICKED_ITEMS)
        return
      }

      if (nextStatus === 'packed' && unpackedItems.length > 0) {
        toastError(ERROR_MESSAGES.UNPACKED_ITEMS)
        return
      }

      // Process packages if transitioning to packed
      let packageTicketUrls: Map<string, { ticketUrl: string; carrierId: string }> | undefined
      if (nextStatus === 'packed') {
        const sendResult = await sendPackages(currentOrder?.packages)
        if (!sendResult.success) {
          return
        }
        packageTicketUrls = sendResult.packageTicketUrls
      }

      const committed = await commitProcessStatus(nextStatus, note)
      if (!committed) {
        return
      }

      if (nextStatus === 'packed') {
        // Use ticketUrls from mutation responses instead of stale cache data
        const { success } = await printPackageTickets(currentOrder?.packages, packageTicketUrls)
        if (!success) {
          toastError(ERROR_MESSAGES.PRINT_ERROR)
          return
        }
      }
    },
    [unpickedItems, unpackedItems, currentOrder?.packages, commitProcessStatus, sendPackages, printPackageTickets],
  )

  /**
   * Return order from picking_error to the status it had before the error.
   * No validations or package side effects - the order already went through them.
   */
  const restoreStatusBeforeError = useCallback(async () => {
    await commitProcessStatus(statusBeforeError)
  }, [commitProcessStatus, statusBeforeError])

  /**
   * Cancel order reservation
   */
  const cancelReservation = useCallback(async () => {
    // TODO: Replace confirm with proper modal
    if (!confirm(ERROR_MESSAGES.CANCEL_RESERVATION)) {
      return
    }

    await core?.services.order.deleteReservation(currentOrder)
    queryClient.invalidateQueries({ queryKey: ['order', currentOrder?.id] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    history.push(getRedirectPath(currentProcessStatus, group))
  }, [core?.services.order, currentOrder, currentProcessStatus, group, history, queryClient])

  useEffect(() => {
    pendingAutoTransition.current = false
    blockedAutoTransition.current = null

    if (!autoProcessStatusChange || !currentOrder?.id) {
      return
    }

    const orderId = Number(currentOrder.id)
    return subscribeToPickingActivity(event => {
      if (event.orderId !== orderId) return
      pendingAutoTransition.current = true
    })
  }, [autoProcessStatusChange, currentOrder?.id])

  useEffect(() => {
    if (!autoProcessStatusChange) return
    if (!currentOrder) return
    if (!blockedAutoTransition.current) return

    const shouldRetryPackageInit =
      blockedAutoTransition.current === 'packageValidation' && validateOrderForPackageInit(currentOrder).isValid
    const shouldRetryCarrierMissing = blockedAutoTransition.current === 'missingCarrier' && canUpdateProcessStatus

    if (shouldRetryPackageInit || shouldRetryCarrierMissing) {
      blockedAutoTransition.current = null
      pendingAutoTransition.current = true
    }
  }, [
    autoProcessStatusChange,
    canUpdateProcessStatus,
    currentOrder,
    currentOrder?.carrier?.id,
    currentOrder?.shippingAddress?.id,
  ])

  useEffect(() => {
    if (!autoProcessStatusChange) return
    if (!currentOrder?.id) return
    const isRelevantStage = currentProcessStatus === 'picking' || currentProcessStatus === 'packing'
    if (!isRelevantStage) {
      pendingAutoTransition.current = false
      blockedAutoTransition.current = null
      return
    }
    if (isProcessingChanges) return
    if (!pendingAutoTransition.current) return

    const isPickingComplete = currentProcessStatus === 'picking' && unpickedItems.length === 0
    const isPackingComplete = currentProcessStatus === 'packing' && unpackedItems.length === 0

    if (!isPickingComplete && !isPackingComplete) {
      return
    }

    const releaseProcessing = (delay = PROCESS_STATUS_DELAY.DEFAULT) => {
      setTimeout(() => (isProcessingChanges = false), delay)
    }

    isProcessingChanges = true

    if (isPickingComplete) {
      const validation = validateOrderForPackageInit(currentOrder)
      if (!validation.isValid) {
        blockedAutoTransition.current = 'packageValidation'
        pendingAutoTransition.current = false
        alert(buildPackageInitErrorMessage(validation.errors))
        releaseProcessing(PROCESS_STATUS_DELAY.ERROR)
        return
      }

      blockedAutoTransition.current = null
      pendingAutoTransition.current = false
      updateProcessStatus('packing')
      releaseProcessing()
      return
    }

    if (isPackingComplete) {
      if (!canUpdateProcessStatus) {
        blockedAutoTransition.current = 'missingCarrier'
        pendingAutoTransition.current = false
        toastWarning(ERROR_MESSAGES.AUTO_UPDATE_BLOCKED, {
          autoClose: TOAST_CONFIG.WARNING_AUTO_CLOSE,
        })
        releaseProcessing()
        return
      }

      blockedAutoTransition.current = null
      pendingAutoTransition.current = false
      updateProcessStatus('packed')
      releaseProcessing()
    }
  }, [
    autoProcessStatusChange,
    currentOrder,
    currentProcessStatus,
    unpickedItems,
    unpackedItems,
    canUpdateProcessStatus,
    updateProcessStatus,
  ])

  return {
    currentProcessStatus,
    canUpdateProcessStatus,
    btnNextStatusLabel,
    backSteps,
    isLoading,
    updateProcessStatus,
    statusBeforeError,
    restoreStatusBeforeError,
    cancelReservation,
  }
}
